import { useMutation, useQueryClient } from '@tanstack/react-query';
import { financialInstrumentQueryKey } from './keys';
import type { FinancialInstrumentDto, FinancialInstrumentHttpRequest } from '../types';

export interface UseDeleteFinancialInstrumentParams {
  documentId: string;
  /** Endpoint base de child (`/{id}/financial-instrument`); se le añade `/{fiId}`. */
  financialInstrumentBaseEndpoint: string;
  httpRequest: FinancialInstrumentHttpRequest;
}

/**
 * Desactiva el Instrumento Financiero activo
 * (`DELETE /{id}/financial-instrument/{fiId}`) → 204 No Content (sin envelope —
 * divergencia consciente de ADR-011, ver spec §4.3). Es un soft-delete
 * (`isActive = false`) que libera el slot para capturar uno nuevo.
 *
 * - `onSuccess`: limpia el IF del cache (vuelve a modo crear).
 * - `onSettled`: invalida `['financial-instrument', documentId]`.
 */
export function useDeleteFinancialInstrument({
  documentId,
  financialInstrumentBaseEndpoint,
  httpRequest,
}: UseDeleteFinancialInstrumentParams) {
  const queryClient = useQueryClient();
  const key = financialInstrumentQueryKey(documentId);

  return useMutation<string, Error, string>({
    mutationFn: async (financialInstrumentId) => {
      await httpRequest('DELETE', `${financialInstrumentBaseEndpoint}/${financialInstrumentId}`);
      return financialInstrumentId;
    },
    onSuccess: () => {
      queryClient.setQueryData<FinancialInstrumentDto | null>(key, null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
